// Secure Frontend Cache Utility
// SECURITY: Never caches tokens, only data. Uses hashed keys.

class SecureCache {
  constructor() {
    this.cache = new Map();
    this.MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB max cache size
    this.currentSize = 0;

    // Resource-specific TTL configuration (milliseconds)
    this.TTL_CONFIG = {
      businessProfile: 5 * 60 * 1000,     // 5 minutes
      employees: 2 * 60 * 1000,           // 2 minutes (changes frequently)
      mvv: 10 * 60 * 1000,                // 10 minutes (rarely changes)
      hrPolicies: 10 * 60 * 1000,         // 10 minutes (rarely changes)
      clockStatus: 30 * 1000,             // 30 seconds (real-time data)
      default: 5 * 60 * 1000              // 5 minutes default
    };
  }

  /**
   * Generate secure cache key from businessId (never use raw tokens)
   * SECURITY: Hashes businessId to prevent sensitive data exposure
   */
  _generateKey(resourceType, businessId) {
    if (!businessId) {
      throw new Error('businessId required for cache key generation');
    }

    // Simple hash for cache key (not cryptographic, just for obfuscation)
    const hash = this._simpleHash(businessId);
    return `${resourceType}_${hash}`;
  }

  /**
   * Simple hash function for cache keys
   */
  _simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Estimate size of cached data in bytes
   */
  _estimateSize(data) {
    const str = JSON.stringify(data);
    return str.length * 2; // Rough estimate (UTF-16 = 2 bytes per char)
  }

  /**
   * Check if cached item is still valid
   */
  _isValid(cacheEntry) {
    if (!cacheEntry) return false;
    const now = Date.now();
    return now - cacheEntry.timestamp < cacheEntry.ttl;
  }

  /**
   * Get data from cache
   * SECURITY: Returns null if expired or missing
   */
  get(resourceType, businessId) {
    const key = this._generateKey(resourceType, businessId);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (!this._isValid(entry)) {
      this.cache.delete(key);
      this.currentSize -= entry.size;
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache
   * SECURITY: Enforces size limits, uses TTL, never accepts tokens
   */
  set(resourceType, businessId, data) {
    // SECURITY: Validate data doesn't contain tokens
    if (this._containsSensitiveData(data)) {
      console.error('❌ Attempted to cache sensitive data - blocked!');
      return false;
    }

    const key = this._generateKey(resourceType, businessId);
    const size = this._estimateSize(data);
    const ttl = this.TTL_CONFIG[resourceType] || this.TTL_CONFIG.default;

    // Check if adding this would exceed max size
    if (this.currentSize + size > this.MAX_CACHE_SIZE) {
      console.warn('⚠️  Cache size limit reached, evicting oldest entries');
      this._evictOldest();
    }

    // Remove old entry if exists
    const oldEntry = this.cache.get(key);
    if (oldEntry) {
      this.currentSize -= oldEntry.size;
    }

    // Add new entry
    this.cache.set(key, {
      data: data,
      timestamp: Date.now(),
      ttl: ttl,
      size: size
    });

    this.currentSize += size;
    return true;
  }

  /**
   * SECURITY: Detect if data contains tokens or sensitive info
   */
  _containsSensitiveData(data) {
    const str = JSON.stringify(data).toLowerCase();

    // Check for common token patterns
    const sensitivePatterns = [
      'token',
      'password',
      'refreshkey',
      'secret',
      'bearer',
      'authorization'
    ];

    return sensitivePatterns.some(pattern => str.includes(pattern));
  }

  /**
   * Evict oldest cache entries (LRU strategy)
   */
  _evictOldest() {
    const entries = Array.from(this.cache.entries());

    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    // Remove oldest 25% of entries
    const toRemove = Math.ceil(entries.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      const [key, entry] = entries[i];
      this.cache.delete(key);
      this.currentSize -= entry.size;
    }

  }

  /**
   * Invalidate specific resource
   */
  invalidate(resourceType, businessId) {
    const key = this._generateKey(resourceType, businessId);
    const entry = this.cache.get(key);

    if (entry) {
      this.cache.delete(key);
      this.currentSize -= entry.size;
      return true;
    }

    return false;
  }

  /**
   * Clear all cache (SECURITY: called on logout)
   */
  clear() {
    this.cache.clear();
    this.currentSize = 0;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      entries: this.cache.size,
      sizeKB: (this.currentSize / 1024).toFixed(2),
      maxSizeMB: (this.MAX_CACHE_SIZE / 1024 / 1024).toFixed(2)
    };
  }
}

// Create singleton instance
const cache = new SecureCache();

export default cache;
